/**
 * OneNote → Notities importeren (Joris, 16 sep 2026).
 *
 * Leest het gedeelde notulen-notebook (SharePoint, delegated Notes.Read.All — app-only kan
 * niet meer) en zet pagina's om naar notities in de tool: op projectniveau als er een
 * projectnummer in titel of sectie staat, anders op accountniveau als de klantnaam matcht.
 * Alles eerst als voorstel in beeld; de gebruiker corrigeert en vinkt aan. Eerder
 * geïmporteerde pagina's herkennen we aan de bronregel "onenote:<pageId>" in de notitie.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, LinearProgress, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';

import PageHeader from '../../ui-components/PageHeader';
import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL } from '../../utils/apiConfig';
import { acquireGraphToken } from '../../utils/graphToken';
import { htmlToPlainText } from '../../utils/htmlText';

const GRAPH = 'https://graph.microsoft.com/v1.0';
const ONENOTE_SITE_ID = 'fullframe.sharepoint.com,b33b3f95-d2fc-4143-ad32-e9c9c7d08d60,471255f6-68ca-4b1b-a292-9ff9f7ff1f58';
const SITE = `${GRAPH}/sites/${encodeURIComponent(ONENOTE_SITE_ID)}/onenote`;

const MARKER = '✅ intern · ';
const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const PROJECTNR = /\b([5-9]\d{3})\b/g;
const STOP = new Set(['full', 'frame', 'fullframe', 'notulen', 'overleg', 'meeting', 'intern', 'algemeen', 'klant', 'team', 'de', 'het', 'een', 'en', 'van']);

async function graphAll(url, headers, max = 5000) {
  const uit = [];
  let next = url; let pogingen = 0;
  while (next && uit.length < max) {
    const r = await fetch(next, { headers });        // eslint-disable-line no-await-in-loop
    if (r.status === 429 || r.status === 503) {      // begrensd wachten met Retry-After (Codex #10)
      if (++pogingen > 6) throw new Error('Graph blijft throttlen (429) — probeer het later opnieuw');
      const wacht = Math.min(30, Number(r.headers.get('Retry-After')) || 2 * pogingen);
      await new Promise((res) => setTimeout(res, wacht * 1000));                                   // eslint-disable-line no-await-in-loop
      continue;
    }
    if (!r.ok) throw new Error(`Graph ${r.status}: ${(await r.text()).slice(0, 160)}`);
    pogingen = 0;
    const d = await r.json();                          // eslint-disable-line no-await-in-loop
    uit.push(...(d.value || []));
    next = d['@odata.nextLink'] || null;
  }
  if (next) uit.afgekapt = true;                       // meer dan `max` items: melden i.p.v. stil stoppen
  return uit;
}

export default function OneNoteImport() {
  const navigate = useNavigate();
  const [stap, setStap] = useState('idle');   // idle | laden | klaar | importeren
  const [voortgang, setVoortgang] = useState('');
  const [fout, setFout] = useState('');
  const [accounts, setAccounts] = useState([]);   // { account, bedrijven[] }
  const [projecten, setProjecten] = useState([]); // { id, nummer, naam, klant }
  const [rijen, setRijen] = useState([]);         // pagina's met voorstel
  const [, setBestaand] = useState(new Set());
  const [filter, setFilter] = useState('');
  const [alleenGematcht, setAlleenGematcht] = useState(true);
  // Na import de OneNote-pagina zelf markeren (titel krijgt '✅ intern · '): vraagt Notes.ReadWrite.All.
  const [markeerOneNote, setMarkeerOneNote] = useState(true);

  const doelen = useMemo(() => [
    ...accounts.map((a) => ({ key: `acc:${a.account}`, label: `🏢 ${a.account}`, type: 'account', account: a.account })),
    ...projecten.map((p) => ({ key: `prj:${p.id}`, label: `📁 ${p.nummer} ${p.naam}`, type: 'project', projectId: p.id, nummer: p.nummer })),
  ], [accounts, projecten]);

  // Pure functie met de lijsten als argument: bij het eerste inlezen waren de states nog
  // leeg (stale closure) en kreeg geen enkele pagina een voorstel.
  const stelVoorMet = (titel, sectie, accs, prjs) => {
    if (/^\s*template\b/i.test(titel || '')) return null;   // sjabloonpagina's nooit voorstellen (16 sep: 6× "Template - ALGEMEEN …" → Enexis)
    const tekst = `${sectie || ''} ${titel || ''}`;
    const nrs = [...tekst.matchAll(PROJECTNR)].map((m) => m[1]);
    for (const nr of nrs) {
      const p = prjs.find((x) => x.nummer === nr);
      if (p) return { key: `prj:${p.id}`, reden: `projectnummer ${nr}` };
    }
    const t = ` ${norm(tekst)} `;
    let beste = null;
    for (const a of accs) {
      for (const naam of [a.account, ...(a.bedrijven || [])]) {
        const n = norm(naam);
        if (n.length < 3 || STOP.has(n)) continue;
        if (t.includes(` ${n} `) && (!beste || n.length > beste.len)) beste = { key: `acc:${a.account}`, len: n.length, reden: `klantnaam “${naam}”` };
      }
    }
    return beste ? { key: beste.key, reden: beste.reden } : { key: '', reden: '' };
  };

  const laad = async () => {
    setStap('laden'); setFout(''); setRijen([]);
    try {
      setVoortgang('Accounts en projecten ophalen…');
      const [boomR, projR, bestaandR] = await Promise.all([
        authFetch(`${BACKEND_URL}/Api/project-notes/boom`),
        authFetch(`${BACKEND_URL}/Api/projects?includeArchived=true`),
        authFetch(`${BACKEND_URL}/Api/project-notes/zoek?q=onenote:`),
      ]);
      const boom = boomR.ok ? await boomR.json() : { accounts: [] };
      const accs = (boom.accounts || []).map((a) => ({ account: a.account || a.naam, bedrijven: Array.isArray(a.bedrijven) ? a.bedrijven : [] })).filter((a) => a.account);
      setAccounts(accs);
      const pj = projR.ok ? await projR.json() : { projects: [] };
      const prjs = (pj.projects || []).map((p) => {
        const f = p.fields || p;
        return { id: p.id || f.id, nummer: String(f.Projectnummer || f.projectNumber || f.nummer || '').trim(), naam: f.Projectnaam || f.projectName || f.naam || '', klant: f.KlantnaamString || f.klant || '' };
      }).filter((p) => p.id && p.nummer);
      setProjecten(prjs);
      const bst = new Set();
      if (!bestaandR.ok) throw new Error('Kon niet nagaan welke pagina\'s al geïmporteerd zijn — import gestopt om dubbelen te voorkomen');
      if (bestaandR.ok) {
        const d = await bestaandR.json();
        for (const n of (d.notes || d.treffers || d.results || [])) {
          const m = String(n.notes || '').match(/onenote:([A-Za-z0-9!._-]+)/g) || [];
          m.forEach((x) => bst.add(x.replace('onenote:', '')));
        }
      }
      setBestaand(bst);

      setVoortgang('OneNote-secties ophalen…');
      const token = await acquireGraphToken(['Notes.Read.All']);
      const headers = { Authorization: `Bearer ${token}` };
      const secties = await graphAll(`${SITE}/sections?$select=id,displayName,parentNotebook&$top=100`, headers);
      const uit = [];
      let i = 0;
      const werker = async () => {
        while (i < secties.length) {
          const s = secties[i++];
          setVoortgang(`Pagina's ophalen… sectie ${i}/${secties.length}: ${s.displayName}`);
          try {
            const paginas = await graphAll(`${SITE}/sections/${s.id}/pages?$select=id,title,createdDateTime,lastModifiedDateTime,links&$top=100`, headers); // eslint-disable-line no-await-in-loop
            if (paginas.afgekapt) toast.error(`Sectie “${s.displayName}” heeft meer pagina's dan ingelezen`);
            paginas.forEach((p) => uit.push({
              id: p.id, titel: p.title || '(zonder titel)', sectie: s.displayName || '', notebook: s.parentNotebook?.displayName || '',
              gemaakt: (p.createdDateTime || '').slice(0, 10), gewijzigd: (p.lastModifiedDateTime || '').slice(0, 10),
              webUrl: p.links?.oneNoteWebUrl?.href || null,
            }));
          } catch (e) { uit.push({ id: `fout-${s.id}`, titel: `⚠ sectie “${s.displayName}” niet gelezen: ${e.message}`, sectie: s.displayName, fout: true }); }
        }
      };
      await Promise.all([werker(), werker(), werker()]);
      if (secties.afgekapt) toast.error('Meer secties dan verwerkt — notebook is erg groot');
      uit.sort((a, b) => String(b.gewijzigd).localeCompare(String(a.gewijzigd)));
      setRijen(uit.map((r) => {
        const v = r.fout ? { key: '', reden: '' } : stelVoorMet(r.titel, r.sectie, accs, prjs);
        const alGedaan = bst.has(r.id) || String(r.titel).startsWith(MARKER);
        return { ...r, doel: v.key, reden: v.reden, aan: Boolean(v.key) && !alGedaan && !r.fout, alGedaan, status: '' };
      }));
      setStap('klaar');
      setVoortgang('');
    } catch (e) {
      setFout(e.message); setStap('idle');
    }
  };

  const importeer = async () => {
    const teDoen = rijen.filter((r) => r.aan && r.doel && !r.alGedaan && !r.fout);
    if (!teDoen.length) { toast.error('Niets aangevinkt'); return; }
    setStap('importeren');
    let token;
    try {
      token = await acquireGraphToken([markeerOneNote ? 'Notes.ReadWrite.All' : 'Notes.Read.All']);
    } catch (e) { toast.error(`Geen toegang tot OneNote: ${e.message}`); setStap('klaar'); return; }
    let ok = 0, mis = 0, n = 0;
    const zet = (id, patch) => setRijen((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const werker = async () => {
      while (n < teDoen.length) {
        const r = teDoen[n++];
        setVoortgang(`Importeren ${ok + mis + 1}/${teDoen.length}: ${r.titel}`);
        try {
          const res = await fetch(`${SITE}/pages/${r.id}/content`, { headers: { Authorization: `Bearer ${token}` } }); // eslint-disable-line no-await-in-loop
          if (!res.ok) throw new Error(`inhoud ${res.status}`);
          const tekst = htmlToPlainText(await res.text()).trim();       // eslint-disable-line no-await-in-loop
          const doel = doelen.find((d) => d.key === r.doel);
          const body = {
            title: r.titel.slice(0, 100),
            notes: `${tekst || '(lege pagina)'}\n\n—\nBron: OneNote · ${r.notebook ? `${r.notebook} › ` : ''}${r.sectie}${r.webUrl ? ` · ${r.webUrl}` : ''}\nonenote:${r.id}`,
            source: 'OneNote', notitiedatum: r.gemaakt || r.gewijzigd || undefined,
            ...(doel?.type === 'project' ? { projectId: doel.projectId } : { klantNaam: doel?.account }),
          };
          const p = await authFetch(`${BACKEND_URL}/Api/project-notes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); // eslint-disable-line no-await-in-loop
          if (!p.ok) throw new Error(`opslaan ${p.status}`);
          let markering = '';
          if (markeerOneNote && !String(r.titel).startsWith(MARKER)) {
            // OneNote-titel bijwerken: PATCH op het title-element. Mislukt dit, dan is de notitie
            // tóch geïmporteerd (Codex #9) — alleen de markering ontbreekt.
            try {
              const m = await fetch(`${SITE}/pages/${r.id}/content`, {   // eslint-disable-line no-await-in-loop
                method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify([{ target: 'title', action: 'replace', content: `${MARKER}${r.titel}` }]),
              });
              markering = m.ok ? ' · OneNote gemarkeerd' : ` · OneNote niet gemarkeerd (${m.status})`;
            } catch (e) { markering = ` · OneNote niet gemarkeerd (${e.message})`; }
          }
          ok += 1; zet(r.id, { status: `✅${markering}`, alGedaan: true, aan: false });
        } catch (e) { mis += 1; zet(r.id, { status: `❌ ${e.message}` }); }
      }
    };
    await Promise.all([werker(), werker()]);
    setStap('klaar'); setVoortgang('');
    toast[mis ? 'error' : 'success'](`${ok} notities geïmporteerd${mis ? `, ${mis} mislukt` : ''}`, { duration: 6000 });
  };

  const zichtbaar = rijen.filter((r) => (!alleenGematcht || r.doel || r.alGedaan) && (!filter || `${r.titel} ${r.sectie}`.toLowerCase().includes(filter.toLowerCase())));
  const aantalAan = rijen.filter((r) => r.aan).length;

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 1.5, sm: 2.5 }, py: 2 }}>
      <PageHeader icon="sticky_note_2" title="OneNote importeren"
        subtitle="Pagina's uit het gedeelde notulen-notebook als notities in de tool — per project (op projectnummer) of per account (op klantnaam)."
        rightActions={<Button onClick={() => navigate('/notes')} sx={{ textTransform: 'none' }}>← Notities</Button>} />

      {fout && <Alert severity="error" sx={{ mb: 2 }}>{fout}</Alert>}
      {stap === 'idle' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          De eerste keer vraagt Microsoft toestemming om OneNote te lezen. Staat “markeren in OneNote” aan, dan vraagt hij ook schrijfrecht: de titel van een geïmporteerde pagina krijgt dan “✅ intern · ” ervoor, zodat je in OneNote ziet wat al in de tool staat.
        </Alert>
      )}
      <Stack direction="row" spacing={1.5} useFlexGap alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Button variant="contained" disableElevation onClick={laad} disabled={stap === 'laden' || stap === 'importeren'} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {stap === 'laden' ? 'Bezig…' : (rijen.length ? 'Opnieuw inlezen' : 'OneNote inlezen')}
        </Button>
        {rijen.length > 0 && (
          <>
            <TextField size="small" placeholder="Filter op titel of sectie" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ minWidth: 260 }} />
            <Chip label={alleenGematcht ? 'Alleen met voorstel' : 'Alles'} onClick={() => setAlleenGematcht((v) => !v)} variant={alleenGematcht ? 'filled' : 'outlined'} />
            <Chip label={markeerOneNote ? '✅ Markeer in OneNote' : 'Niet markeren in OneNote'} onClick={() => setMarkeerOneNote((v) => !v)} variant={markeerOneNote ? 'filled' : 'outlined'} color={markeerOneNote ? 'success' : 'default'} />
            <Button size="small" onClick={() => setRijen((prev) => prev.map((r) => ({ ...r, aan: Boolean(r.doel) && !r.alGedaan && !r.fout })))} sx={{ textTransform: 'none' }}>Alles met voorstel aanvinken</Button>
            <Button size="small" onClick={() => setRijen((prev) => prev.map((r) => ({ ...r, aan: false })))} sx={{ textTransform: 'none' }}>Niets</Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" color="success" disableElevation onClick={importeer} disabled={!aantalAan || stap !== 'klaar'} sx={{ textTransform: 'none', fontWeight: 700 }}>
              {aantalAan} notities importeren
            </Button>
          </>
        )}
      </Stack>
      {voortgang && <Box sx={{ mb: 2 }}><LinearProgress /><Typography variant="caption" sx={{ color: '#64748b' }}>{voortgang}</Typography></Box>}
      {stap === 'klaar' && (
        <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
          {rijen.length} pagina's · {accounts.length} accounts en {projecten.length} projecten geladen · {rijen.filter((r) => r.doel).length} met voorstel · {rijen.filter((r) => r.alGedaan).length} al eerder geïmporteerd · {rijen.filter((r) => !r.doel && !r.fout).length} zonder match (kies handmatig of sla over)
        </Typography>
      )}
      {rijen.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Sectie</TableCell><TableCell>Titel</TableCell><TableCell>Datum</TableCell>
                <TableCell sx={{ minWidth: 320 }}>Naar</TableCell><TableCell>Waarom</TableCell><TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {zichtbaar.slice(0, 500).map((r) => (
                <TableRow key={r.id} sx={{ opacity: r.alGedaan ? 0.55 : 1 }}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={Boolean(r.aan)} disabled={r.alGedaan || r.fout || !r.doel}
                      onChange={(e) => setRijen((prev) => prev.map((x) => (x.id === r.id ? { ...x, aan: e.target.checked } : x)))} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.sectie}</TableCell>
                  <TableCell>
                    {r.webUrl ? <a href={r.webUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#3730a3' }}>{r.titel}</a> : r.titel}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.gemaakt || r.gewijzigd || ''}</TableCell>
                  <TableCell>
                    {!r.fout && (
                      <Autocomplete size="small" options={doelen} getOptionLabel={(o) => o.label} value={doelen.find((d) => d.key === r.doel) || null}
                        isOptionEqualToValue={(a, b) => a.key === b.key} disabled={r.alGedaan}
                        onChange={(_, v) => setRijen((prev) => prev.map((x) => (x.id === r.id ? { ...x, doel: v?.key || '', reden: v ? 'handmatig' : '', aan: Boolean(v) && !x.alGedaan } : x)))}
                        renderInput={(p) => <TextField {...p} placeholder="Kies account of project…" />} />
                    )}
                  </TableCell>
                  <TableCell><Typography variant="caption" sx={{ color: '#64748b' }}>{r.reden}</Typography></TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {r.alGedaan && !r.status ? <Tooltip title="Al eerder geïmporteerd"><span>✅ al gedaan</span></Tooltip> : r.status}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {zichtbaar.length > 500 && <Typography variant="caption" sx={{ color: '#64748b' }}>Eerste 500 van {zichtbaar.length} getoond — gebruik het filter.</Typography>}
        </Box>
      )}
    </Box>
  );
}
